import { lazy, Suspense, useEffect, useState } from "react";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { ToastContainer } from "react-toastify";

// Eager load critical components
import AdminProtectedRoute from "./admin/routes/AdminProtectedRoute.jsx";
import { AdminFooter, AdminNavbar, AdminSidebar } from "./admin/routes/AdminRoutes.jsx";
import LoadingSpinner from "./components/loading/LoadingSpinner.jsx";
import PageNotFound from "./components/page-not-found/PageNotFound.jsx";
import UnauthorizedPage from "./components/unauthorized/UnauthorizedPage.jsx";
import { ClientFooter } from "./routes/ClientRoutes.jsx";
import PlanProtectedRoute from "./admin/routes/PlanProtectedRoute.jsx";

// Redux imports
import { useDispatch, useSelector } from "react-redux";
import { getAllInventories } from "./redux/actions/inventoryAction.js";
import { getAllProducts } from "./redux/actions/productAction.js";
import { getSingleStoreItems } from "./redux/actions/storeInventoryAction.js";
import { loadUser } from "./redux/actions/userAction.js";
import { loadOrganization } from "./redux/actions/organizationAction.js";
import { getSalesByStore } from "./redux/actions/saleAction.js";
import { getSubscriptionPlan } from "./redux/actions/settingsAction.js";
const CreateOrganizationPage = lazy(() => import("./pages/organization/CreateOrganizationPage.jsx"));

// Lazy load components to enable code splitting
const HomePage = lazy(() => import("./pages/homepage/HomePage.jsx"));
const ContactPage = lazy(() => import("./pages/contact/ContactPage.jsx"));
const RegisterPage = lazy(() => import("./pages/register/RegisterPage.jsx"));


// Admin components - lazy loaded
const AdminDashboard = lazy(() => import("./admin/components/dashboard/AdminDashboard.jsx"));
const AdminLoginPage = lazy(() => import("./admin/pages/login/AdminLoginPage.jsx"));
const ForgotPasswordPage = lazy(() => import("./admin/pages/forgot-password/ForgotPasswordPage.jsx"));
const OtpVerificationPage = lazy(() => import("./admin/pages/otp-verification/OtpVerificationPage.jsx"));
const ChangePasswordPage = lazy(() => import("./admin/pages/change-password/ChangePasswordPage.jsx"));
const UsersPage = lazy(() => import("./admin/pages/users/UsersPage.jsx"));
const UserProfilePage = lazy(() => import("./admin/pages/user-profile/UserProfilePage.jsx"));
const SettingsPage = lazy(() => import("./admin/pages/settings/SettingsPage.jsx"));
const OrganizationProfilePage = lazy(() => import("./admin/pages/organization/OrganizationProfilePage.jsx"));

const SubcategoriesPage = lazy(() => import("./admin/pages/subcategories/SubcategoriesPage.jsx"));
const ProductsPage = lazy(() => import("./admin/pages/products/ProductsPage.jsx"));
const InventoryPage = lazy(() => import("./admin/pages/inventory/InventoryPage.jsx"));
const CreateInventoryPage = lazy(() => import("./admin/pages/inventory/CreateInventoryPage.jsx"));
const EditInventoryPage = lazy(() => import("./admin/pages/inventory/EditInventoryPage.jsx"));

const PosDashboard = lazy(() => import("./admin/pages/pos-dashboard/PosDashboard.jsx"));
const StoresPage = lazy(() => import("./admin/pages/stores/StoresPage.jsx"));
const StoreInventoryPage = lazy(() => import("./admin/pages/store-inventory/StoreInventoryPage.jsx"));
const ViewStoreInventoryPage = lazy(() => import("./admin/pages/inventory/store-inventory/ViewStoreInventoryPage.jsx"));

const SalesPage = lazy(() => import("./admin/pages/sales/SalesPage.jsx"));
const PurchasesPage = lazy(() => import("./admin/pages/purchases/PurchasesPage.jsx"));
const PurchaseDashboard = lazy(() => import("./admin/pages/purchases/purchase-dashboard/PurchaseDashboard.jsx"));
const ViewPurchaseDetailsPage = lazy(() => import("./admin/pages/purchases/ViewPurchaseDetailsPage.jsx"));
const SuppliersPage = lazy(() => import("./admin/pages/suppliers/SuppliersPage.jsx"));
const ExpensesPage = lazy(() => import("./admin/pages/expenses/ExpensesPage.jsx"));
const StaffPage = lazy(() => import("./admin/pages/staff/StaffPage.jsx"));
const ViewStaffPage = lazy(() => import("./admin/pages/staff/ViewStaffPage.jsx"));
const ZReportsPage = lazy(() => import("./admin/pages/z-reports/ZReportsPage.jsx"));
const ShiftsPage = lazy(() => import("./admin/pages/shifts/ShiftsPage.jsx"));
const ViewShiftPage = lazy(() => import("./admin/pages/shifts/ViewShiftPage.jsx"));
const ReportsDashboard = lazy(() => import("./admin/pages/reports/ReportsDashboard.jsx"));
const ReturnsPage = lazy(() => import("./admin/pages/returns/ReturnsPage.jsx"));
const ReturnDashboard = lazy(() => import("./admin/pages/returns/ReturnDashboard.jsx"));
const CustomersPage = lazy(() => import("./admin/pages/customers/CustomersPage.jsx"));
const PaymentsPage = lazy(() => import("./admin/pages/payments/PaymentsPage.jsx"));
const ViewCustomerPage = lazy(() => import("./admin/pages/customers/ViewCustomerPage.jsx"));
const ViewSupplierPage = lazy(() => import("./admin/pages/suppliers/ViewSupplierPage.jsx"));
const FlexpaySalesDashboard = lazy(() => import("./admin/pages/flexpaysales/FlexpaySalesDashboard.jsx"));
const ReconciliationPage = lazy(() => import("./admin/pages/reconciliation/ReconciliationPage.jsx"));
const ReconciliationDetailPage = lazy(() => import("./admin/pages/reconciliation/ReconciliationDetailPage.jsx"));
const ViewInventorySummary = lazy(() => import("./admin/pages/store-inventory/ViewInventorySummary.jsx"));
const FlexPaySalePage = lazy(() => import("./admin/pages/flexpaysales/FlexPaySalePage.jsx"));
const ViewFlexPaySalePage = lazy(() => import("./admin/pages/flexpaysales/ViewFlexPaySalePage.jsx"));

const App = () => {

  // client layout component
  const ClientLayout = () => {
    return (
      <>
        {/* <ClientNavbar /> */}
        <div className="client-layout">
          <Outlet />
        </div>
        <ClientFooter />
      </>
    )
  }

  // admin layout component
  const AdminLayout = () => {
    const dispatch = useDispatch();
    const [showSidebar, setShowSidebar] = useState(false);
    const { isAuthenticated, currentUser } = useSelector((state) => state.user);

    const storeId = currentUser?.store?._id;

    useEffect(() => {
      dispatch(loadUser());
      dispatch(loadOrganization());
      dispatch(getSubscriptionPlan());
    }, [dispatch]);

    useEffect(() => {
      if (!isAuthenticated || !storeId) return;

      dispatch(getAllProducts());
      dispatch(getAllInventories(storeId));
      dispatch(getSingleStoreItems(storeId));
      dispatch(getSalesByStore(storeId));

    }, [dispatch, isAuthenticated, storeId]);

    const handleCloseSidebar = () => setShowSidebar(false);
    const handleShowSidebar = () => setShowSidebar(true);

    return (
      <>
        <AdminNavbar handleShowSidebar={handleShowSidebar} />
        <AdminSidebar show={showSidebar} handleClose={handleCloseSidebar} handleShow={handleShowSidebar} />
        <div className="admin-layout">
          <Outlet />
        </div>
        <AdminFooter />
      </>
    );
  };

  // Define routes using createBrowserRouter
  const router = createBrowserRouter([
    {
      path: "/create-organization",
      element: (
        <Suspense fallback={<LoadingSpinner />}>
          <CreateOrganizationPage />
        </Suspense>
      )
    },
    {
      path: "/register",
      element: (
        <Suspense fallback={<LoadingSpinner />}>
          <RegisterPage />
        </Suspense>
      )
    },
    {
      path: "/",
      element: <ClientLayout />,
      children: [
        {
          index: true,
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <HomePage />
            </Suspense>
          )
        },
        {
          path: "contact-us",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <ContactPage />
            </Suspense>
          )
        },
      ]
    },
    {
      path: "/admin/login",
      element: (
        <Suspense fallback={<LoadingSpinner />}>
          <AdminLoginPage />
        </Suspense>
      )
    },
    {
      path: "/admin/forgot-password",
      element: (
        <Suspense fallback={<LoadingSpinner />}>
          <ForgotPasswordPage />
        </Suspense>
      )
    },
    {
      path: "/admin/otp-verification",
      element: (
        <Suspense fallback={<LoadingSpinner />}>
          <OtpVerificationPage />
        </Suspense>
      )
    },
    {
      path: "/admin/change-password",
      element: (
        <Suspense fallback={<LoadingSpinner />}>
          <ChangePasswordPage />
        </Suspense>
      )
    },
    {
      path: "/admin",
      element: (
        <AdminProtectedRoute>
          <AdminLayout />
        </AdminProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <StoreInventoryPage />
            </Suspense>
          )
        },
        {
          path: "pos",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <PosDashboard />
            </Suspense>
          )
        },
        {
          path: "dashboard",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <AdminDashboard />
            </Suspense>
          )
        },
        {
          path: "stores",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <StoresPage />
            </Suspense>
          )
        },
        {
          path: "store-inventory",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <StoreInventoryPage />
            </Suspense>
          )
        },
        {
          path: "store-inventory/view/:id",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <ViewStoreInventoryPage />
            </Suspense>
          )
        },
        {
          path: "store-inventory/view-summary",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <ViewInventorySummary />
            </Suspense>
          )
        },
        {
          path: "store-inventory/reconciliations",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <ReconciliationPage />
            </Suspense>
          )
        },
        {
          path: "store-inventory/reconciliations/:id",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <ReconciliationDetailPage />
            </Suspense>
          )
        },
        {
          path: "subcategories",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <SubcategoriesPage />
            </Suspense>
          )
        },
        {
          path: "products",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <ProductsPage />
            </Suspense>
          )
        },
        {
          path: "purchases",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <PurchasesPage />
            </Suspense>
          )
        },
        {
          path: "purchases/dashboard",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <PurchaseDashboard />
            </Suspense>
          )
        },
        {
          path: "purchases/:id",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <ViewPurchaseDetailsPage />
            </Suspense>
          )
        },
        {
          path: "inventory",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <InventoryPage />
            </Suspense>
          )
        },
        {
          path: "suppliers",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <SuppliersPage />
            </Suspense>
          )
        },
        {
          path: "suppliers/:id",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <ViewSupplierPage />
            </Suspense>
          )
        },
        {
          path: "customers",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <CustomersPage />
            </Suspense>
          )
        },
        {
          path: "payments",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <PaymentsPage />
            </Suspense>
          )
        },
        {
          path: "customers/:id",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <ViewCustomerPage />
            </Suspense>
          )
        },
        {
          path: "inventory/create-inventory",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <CreateInventoryPage />
            </Suspense>
          )
        },
        {
          path: "inventory/edit/:inventoryId",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <EditInventoryPage />
            </Suspense>
          )
        },
        {
          path: "sales",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <SalesPage />
            </Suspense>
          )
        },
        {
          path: "flexpaysales",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <PlanProtectedRoute>
                <FlexPaySalePage />
              </PlanProtectedRoute>
            </Suspense>
          )
        },
        {
          path: "flexpaysales/view/:id",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <PlanProtectedRoute>
                <ViewFlexPaySalePage />
              </PlanProtectedRoute>
            </Suspense>
          )
        },
        {
          path: "flexpaysales/dashboard",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <PlanProtectedRoute>
                <FlexpaySalesDashboard />
              </PlanProtectedRoute>
            </Suspense>
          )
        },
        {
          path: "returns",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <ReturnsPage />
            </Suspense>
          )
        },
        {
          path: "returns/sale-return-dashboard",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <ReturnDashboard />
            </Suspense>
          )
        },
        {
          path: "expenses",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <ExpensesPage />
            </Suspense>
          )
        },
        {
          path: "z-reports",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <ZReportsPage />
            </Suspense>
          )
        },
        {
          path: "shifts",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <ShiftsPage />
            </Suspense>
          )
        },
        {
          path: "shifts/:id",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <ViewShiftPage />
            </Suspense>
          )
        },
        {
          path: "users",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <UsersPage />
            </Suspense>
          )
        },
        {
          path: "staff",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <StaffPage />
            </Suspense>
          )
        },
        {
          path: "staff/view/:id",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <ViewStaffPage />
            </Suspense>
          )
        },
        {
          path: "user-profile",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <UserProfilePage />
            </Suspense>
          )
        },
        {
          path: "reports",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <ReportsDashboard />
            </Suspense>
          )
        },
        {
          path: "settings",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <SettingsPage />
            </Suspense>
          )
        },
        {
          path: "organization-profile",
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <OrganizationProfilePage />
            </Suspense>
          )
        }
      ]
    },
    {
      path: "/admin/unauthorized",
      element: <UnauthorizedPage />
    },
    {
      path: "*",
      element: <PageNotFound />
    }
  ]);

  return (
    <>
      <RouterProvider router={router} />
      <ToastContainer
        autoClose={5000} // Toast will vanish after 5 seconds
        // hideProgressBar={true} // No progress bar
        closeOnClick={true} // Close on click
        pauseOnHover={true} // Do not pause on hover
        newestOnTop={true} // Newest messages appear on top
        style={{ zIndex: 9999 }} // Ensure toasts appear above other elements
      />
    </>
  )
}
export default App